/*-------------------------------------------------------------------------
 *
 * pgEdge MCP Client
 *
 * Copyright (c) 2025 - 2026, pgEdge, Inc.
 * This software is released under The PostgreSQL License
 *
 *-------------------------------------------------------------------------
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import { Container, Box, CircularProgress, CssBaseline, IconButton, Tooltip } from '@mui/material';
import { ChevronRight as ChevronRightIcon, ChevronLeft as ChevronLeftIcon } from '@mui/icons-material';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { useMCPClient } from './hooks/useMCPClient';
import { useConversations } from './hooks/useConversations';
import Header from './components/Header';
import MainContent from './components/MainContent';
import ConversationPanel from './components/ConversationPanel';
import Login from './components/Login';
import { createPgedgeTheme, loginTheme } from './theme/pgedgeTheme';

const AppContent = () => {
  const [mode, setMode] = useState(() => {
    // Load theme preference from localStorage
    const savedMode = localStorage.getItem('theme-mode');
    return savedMode || 'light';
  });
  const [conversationPanelOpen, setConversationPanelOpen] = useState(true);
  const { user, loading, sessionToken } = useAuth();
  const { serverInfo } = useMCPClient(sessionToken);
  const conversations = useConversations(sessionToken);

  // Save theme preference to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('theme-mode', mode);
  }, [mode]);

  // Create theme using pgEdge theme configuration
  const theme = useMemo(() => createPgedgeTheme(mode), [mode]);

  const toggleTheme = () => {
    setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
  };

  const handleConversationsClick = useCallback(() => {
    setConversationPanelOpen(true);
  }, []);

  const handleConversationPanelClose = useCallback(() => {
    setConversationPanelOpen(false);
  }, []);

  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'background.default',
          }}
        >
          <CircularProgress />
        </Box>
      </ThemeProvider>
    );
  }

  if (!user) {
    return (
      <ThemeProvider theme={loginTheme}>
        <CssBaseline />
        <Login />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', bgcolor: 'background.default', overflow: 'hidden' }}>
        <Header
          onToggleTheme={toggleTheme}
          mode={mode}
          serverInfo={serverInfo}
        />
        <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden', position: 'relative' }}>
          {/* Vertical tab button for conversation panel */}
          <Tooltip title={conversationPanelOpen ? "Close history" : "Open history"} placement="right">
            <IconButton
              onClick={conversationPanelOpen ? handleConversationPanelClose : handleConversationsClick}
              aria-label="toggle conversation history"
              sx={{
                position: 'absolute',
                left: 0,
                top: 16,
                zIndex: 1200,
                bgcolor: 'background.paper',
                borderRadius: '0 8px 8px 0',
                boxShadow: 2,
                width: 24,
                height: 48,
                minWidth: 24,
                padding: 0,
                border: '1px solid',
                borderColor: 'divider',
                borderLeft: 'none',
                color: 'primary.main',
                '&:hover': {
                  bgcolor: 'action.hover',
                  color: 'primary.dark',
                },
              }}
            >
              {conversationPanelOpen ? <ChevronLeftIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
          <Container maxWidth="lg" sx={{ flex: 1, display: 'flex', flexDirection: 'column', py: 2, overflow: 'hidden' }}>
            <MainContent conversations={conversations} />
          </Container>
        </Box>
      </Box>
      <ConversationPanel
        open={conversationPanelOpen}
        onClose={handleConversationPanelClose}
        conversations={conversations.conversations}
        currentConversationId={conversations.currentConversationId}
        onSelect={conversations.selectConversation}
        onNewConversation={conversations.startNewConversation}
        onRename={conversations.renameConversation}
        onDelete={conversations.deleteConversation}
        onDeleteAll={conversations.deleteAllConversations}
        loading={conversations.loading}
        disabled={false}
      />
    </ThemeProvider>
  );
};

const App = () => {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
};

export default App;
